/**
 * Higher-level Clarity signals: route views, API failures, UI errors, performance.
 * All compose the core wrapper (clarity.ts) + the finite-cardinality taxonomy (events.ts).
 * Every value sent here is bucketed, enum'd, or endpoint-normalized — no raw ids/timings/URLs.
 */

import { setClarityTags, trackClarityEvent } from './clarity';
import {
  classifyApiFailure,
  durationBucket,
  normalizeEndpoint,
  pageType,
  perfBucket,
  routeGroup,
  type ClarityEvent,
} from './events';

// ── Route views ──────────────────────────────────────────────────────────────
export interface RouteContext {
  pathname: string;
  /** Coarse auth state for filtering: 'anonymous' | 'authenticated' | 'impersonating'. */
  authState?: 'anonymous' | 'authenticated' | 'impersonating';
}

/** Tag the current route (route_id/group/page_type) and fire `route-viewed`. */
export function trackRouteView(ctx: RouteContext): void {
  const group = routeGroup(ctx.pathname);
  setClarityTags({
    route_id: normalizeEndpoint(ctx.pathname),
    route_group: group,
    page_type: pageType(ctx.pathname, group),
    auth_state: ctx.authState,
  });
  trackClarityEvent('route-viewed');
}

// ── API failures ─────────────────────────────────────────────────────────────
export interface ApiFailureContext {
  method: string;
  /** Raw request path; normalized to `/x/:id` before it leaves the app. */
  path: string;
  status?: number;
  isTimeout?: boolean;
  /** A refresh/retry was attempted. */
  retried?: boolean;
  /** The retry ultimately succeeded (also fires `api-retry-succeeded`). */
  recovered?: boolean;
}

const STATUS_CLASS = (status?: number, isTimeout?: boolean): string => {
  if (isTimeout) return 'timeout';
  if (!status) return 'network';
  if (status >= 500) return '5xx';
  if (status >= 400) return '4xx';
  return '2xx';
};

/** Normalize + tag an API failure and fire the matching `api-*` event. */
export function trackApiFailure(ctx: ApiFailureContext): void {
  const { event, health } = classifyApiFailure(ctx.status, ctx.isTimeout);
  const endpoint = `${ctx.method.toUpperCase()} ${normalizeEndpoint(ctx.path)}`;
  setClarityTags({
    last_error_type: event,
    last_error_area: endpoint,
    api_health_bucket: health,
    http_status_class: STATUS_CLASS(ctx.status, ctx.isTimeout),
  });
  trackClarityEvent(event, { error_endpoint: endpoint });
  if (ctx.recovered) trackClarityEvent('api-retry-succeeded', { error_endpoint: endpoint });
}

// ── UI errors ────────────────────────────────────────────────────────────────
export interface UiErrorContext {
  /** Module/area where the boundary caught, e.g. 'app-root' | 'dashboard'. */
  area?: string;
  /** Normalized route the error happened on (defaults to current path, normalized). */
  route?: string;
  /** Error class name only — never the message/stack (PII / data leak risk). */
  errorName?: string;
  event?: Extract<ClarityEvent, 'ui-error-boundary-hit' | 'render-failed'> | ClarityEvent;
}

/** Tag + fire a UI error. Only safe metadata (area, route, error class) is sent. */
export function trackUiError(ctx: UiErrorContext = {}): void {
  const route =
    ctx.route ??
    (typeof window !== 'undefined' ? normalizeEndpoint(window.location.pathname) : undefined);
  setClarityTags({
    last_error_type: 'ui-error-boundary-hit',
    last_error_area: ctx.area,
    error_route: route,
    error_name: ctx.errorName,
  });
  trackClarityEvent(ctx.event ?? 'ui-error-boundary-hit');
}

// ── Performance ──────────────────────────────────────────────────────────────
export interface PerformanceContext {
  kind: 'route' | 'api' | 'interaction' | 'long-task' | 'web-vital';
  durationMs: number;
  /** Optional area/label, e.g. the route group or normalized endpoint. */
  label?: string;
  /** Threshold (ms) above which it counts as slow for THIS kind. */
  slowMs?: number;
}

const PERF_EVENT: Record<PerformanceContext['kind'], ClarityEvent> = {
  route: 'performance-slow-route',
  api: 'performance-slow-api',
  interaction: 'performance-slow-interaction',
  'long-task': 'performance-long-task',
  'web-vital': 'performance-poor-web-vital',
};

/**
 * Record a bucketed performance signal. Fires the `performance-*` event ONLY when the
 * measurement is actually slow (>= slowMs), so funnels/recordings surface friction, not noise.
 */
export function trackPerformanceSignal(ctx: PerformanceContext): void {
  const slowMs = ctx.slowMs ?? 2000;
  setClarityTags({
    performance_bucket: perfBucket(ctx.durationMs, slowMs),
    [`perf_${ctx.kind.replace('-', '_')}_bucket`]: durationBucket(ctx.durationMs),
  });
  if (ctx.kind === 'long-task' || ctx.durationMs >= slowMs) {
    trackClarityEvent(PERF_EVENT[ctx.kind], { perf_area: ctx.label });
  }
}
